import * as React from 'react'

import { SearchIcon, XIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationAttributeBase from '~/client/src/shared/models/LocationObjects/LocationAttributeBase'
import { ITag } from '~/client/src/shared/models/Tag'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

interface IProps {
  value: string
  onChange: (newValue: string) => void
  onReset: () => void
  onTagClick: () => void
  pivotalTag?: ITag
  customPlaceholder?: string
  className?: string
  useAlternativeLayout?: boolean
  customSearchIcon?: React.ReactNode
  onFocus?: () => void
  onBlur?: () => void
  autoFocus?: boolean
  /** data-testid for the input element (opt-in; e2e hook). */
  inputTestId?: string
}

@observer
export default class SearchBar extends React.Component<IProps> {
  public render() {
    const {
      value,
      onReset,
      className,
      useAlternativeLayout,
      customSearchIcon,
    } = this.props

    return (
      <div
        className={classList({
          'tw:flex tw:items-center search-bar-container tw:bg-background tw:[&_*]:outline-none tw:rounded-[24px] tw:p-3 tw:text-body-md tw:text-muted-foreground tw:[&_.hierarchy-delivery-attribute-tag-content]:max-w-[70px]': true,
          'tw:!py-0 tw:!pr-0 tw:!pl-1.5 tw:!rounded-[4px] tw:!border tw:!border-solid tw:!border-border tw:!bg-transparent tw:!shadow-none tw:h-8':
            useAlternativeLayout,
          [className]: !!className,
        })}
      >
        {customSearchIcon || (
          <SearchIcon
            aria-hidden="true"
            className={classList({
              'tw:size-4 tw:shrink-0 tw:mr-2': true,
              'tw:text-primary': !!value,
              'tw:text-muted-foreground': !value,
            })}
          />
        )}
        {this.renderInputArea()}
        {!!value && (
          <XIcon
            aria-label={Localization.translator.clear}
            className="tw:size-4 tw:shrink-0 tw:ml-1 tw:cursor-pointer tw:text-muted-foreground tw:hover:text-foreground"
            onClick={onReset}
          />
        )}
      </div>
    )
  }

  private renderInputArea() {
    const { pivotalTag, value, onTagClick } = this.props

    return (
      <>
        {pivotalTag && (
          <span
            className="tw:flex tw:items-center tw:flex-none tw:text-body-md tw:text-foreground tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:mr-2"
            onClick={onTagClick}
          >
            <SitemapAttributeTag
              isRemovable={true}
              dataObject={pivotalTag as LocationAttributeBase}
              shouldShowAsTag={true}
              onRemove={onTagClick}
              contentContainerClassName="text-ellipsis"
            >
              <span>{pivotalTag.name}</span>
            </SitemapAttributeTag>
          </span>
        )}
        <input
          className="tw:p-0 tw:border-none tw:bg-transparent tw:shadow-none tw:text-body-md tw:text-muted-foreground tw:w-full"
          value={value}
          autoFocus={!!pivotalTag || !!this.props.autoFocus}
          placeholder={this.inputPlaceholder}
          onChange={this.handleChangeQuery}
          onFocus={this.props.onFocus}
          onBlur={this.props.onBlur}
          data-testid={this.props.inputTestId}
        />
      </>
    )
  }

  private handleChangeQuery = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onChange(target.value)
  }

  private get inputPlaceholder(): string {
    const { pivotalTag, customPlaceholder } = this.props

    return pivotalTag
      ? EMPTY_STRING
      : customPlaceholder || Localization.translator.searchUserOrGroup
  }
}
